import { z } from "zod";
import { uuidSchema } from "./api.ts";
import { csvArraySchema, ENTITY_LABELS, ENTITY_TYPES, entityTypeSchema, type EntityType } from "./common.ts";

/**
 * Global search (command palette / ⌘K) — GET /api/v1/search?q=&types=&limit=
 *
 * Backed by a derived search index (rebuildable from the source tables; kept
 * fresh by an idempotent outbox consumer). Results only include types the caller
 * can read (ENTITY_READ_PERMISSION) and rows they can see (e.g. project membership).
 * The same index is reused as the keyword-retrieval step of the OpenAI-backed
 * "Ask your business" assistant.
 *
 * ENTITY_TYPES (the product-wide list of searchable/business record types) is
 * defined in common.ts and re-exported from the package root.
 */

export const SEARCH_MAX_QUERY_LENGTH = 200;
export const SEARCH_DEFAULT_LIMIT_PER_TYPE = 5;

export const searchQuerySchema = z.object({
  q: z.string().trim().min(1).max(SEARCH_MAX_QUERY_LENGTH),
  /** Comma-separated or repeated; default = every type the caller can read. */
  types: csvArraySchema(entityTypeSchema, ENTITY_TYPES.length).optional(),
  /** Max results PER TYPE (1–20). */
  limit: z.coerce.number().int().min(1).max(20).default(SEARCH_DEFAULT_LIMIT_PER_TYPE),
});
export type SearchQuery = z.infer<typeof searchQuerySchema>;
export type SearchQueryInput = z.input<typeof searchQuerySchema>;

export const searchResultSchema = z.object({
  entityType: entityTypeSchema,
  entityId: uuidSchema,
  title: z.string(),
  /** Secondary line, e.g. client name for a project or "₹1,20,000 · Due 12 Oct" for an invoice. */
  subtitle: z.string().nullable(),
  /** Web path (ENTITY_ROUTES). */
  url: z.string(),
  /** Relevance, higher is better (comparable within one response only). */
  score: z.number(),
  /** Optional status chip ("Overdue", "Won", "Active"). */
  status: z.string().nullable(),
});
export type SearchResult = z.infer<typeof searchResultSchema>;

/**
 * Response: results are GROUPED (contiguous per entityType) and ORDERED — groups
 * by their best score desc, results within a group by score desc.
 * `groupSearchResults` rebuilds the grouped structure for rendering.
 */
export const searchResponseSchema = z.object({
  query: z.string(),
  results: z.array(searchResultSchema),
  tookMs: z.number().int().nonnegative(),
});
export type SearchResponse = z.infer<typeof searchResponseSchema>;

export interface SearchResultGroup {
  entityType: EntityType;
  label: string;
  results: SearchResult[];
}

/** Pure: groups + orders results (server uses it before responding; web to render). */
export function groupSearchResults(results: readonly SearchResult[]): SearchResultGroup[] {
  const byType = new Map<EntityType, SearchResult[]>();
  for (const r of results) {
    const list = byType.get(r.entityType);
    if (list) list.push(r);
    else byType.set(r.entityType, [r]);
  }
  const groups: SearchResultGroup[] = [];
  for (const [entityType, list] of byType) {
    list.sort((a, b) => b.score - a.score);
    groups.push({ entityType, label: ENTITY_LABELS[entityType].plural, results: list });
  }
  groups.sort((a, b) => (b.results[0]?.score ?? 0) - (a.results[0]?.score ?? 0));
  return groups;
}

/** Pure: flattens groups back into the wire order. */
export function orderSearchResults(results: readonly SearchResult[]): SearchResult[] {
  return groupSearchResults(results).flatMap((g) => g.results);
}
